import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  Camera,
  CheckCircle,
  AlertCircle,
  FileText,
  DollarSign,
  Plus,
  Trash2,
  Save,
  Send,
  Info,
  RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import SignatureCanvas from 'react-signature-canvas';

const ServiceReportForm = ({ job, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);
  const [reportData, setReportData] = useState({
    dateOfAttendance: new Date().toISOString().split('T')[0],
    workOrderNumber: job?.jobNumber || '',
    customerDetails: {
      name: '',
      telephone: '',
      email: '',
      address: '',
      unitNumber: '',
      postalCode: ''
    },
    preventiveMaintenance: {
      monthlyVisualCheck: {
        performed: false,
        electrical: {
          lightingPoints: false,
          switches: false,
          powerSockets: false,
          dbCoverSecure: false,
          noBurningSigns: false,
          rccbTested: false
        },
        plumbing: {
          exposedPipesLeaks: false,
          exposedPipesCorrosion: false,
          tapsAndFaucets: false,
          toiletFlushing: false,
          bidetSprayer: false,
          drainsClear: false
        },
        fixturesAndFittings: {
          doorHandles: false,
          cabinetHinges: false,
          windowLatches: false
        }
      },
      halfYearlyService: {
        performed: false,
        shortCircuitTest: false,
        nextScheduled: '',
        acFilters: false,
        kitchenHoodFilters: false,
        smokeDetectors: false
      },
      minorHomeFixes: {
        performed: false,
        tightenFittings: false,
        replaceBulbs: false,
        plumbingDrips: false,
        drainClogs: false,
        adjustDoors: false
      }
    },
    adHocWorks: {
      description: '',
      propertyConditionPhotos: [],
      notIncludedInSubscription: true
    },
    charges: {
      adHocWorksTotal: '',
      partsMaterialsCost: '',
      totalAmount: 0
    },
    customerAcknowledgement: {
      signature: '',
      date: new Date().toISOString().split('T')[0]
    }
  });

  // Initialize customer details and load existing service report from job
  useEffect(() => {
    if (job && job.customerId) {
      // Parse address to extract street address and unit number if combined
      let streetAddress = job.location?.address || job.location?.streetAddress || '';
      let unitNum = job.location?.unitNumber || job.location?.unit || '';
      let postalCode = job.location?.postalCode || job.location?.zipCode || '';

      // If unit number is embedded in address (e.g., "Unit #08-88, 123 Kellock Road")
      if (!unitNum && streetAddress.includes('Unit')) {
        const unitMatch = streetAddress.match(/Unit\s*(#?[\d-]+)/i);
        if (unitMatch) {
          unitNum = unitMatch[1];
          streetAddress = streetAddress.replace(/Unit\s*#?[\d-]+,?\s*/i, '').trim();
        }
      }

      // Remove postal code and city from street address if present
      streetAddress = streetAddress
        .replace(/,?\s*\d{6}\s*Singapore/i, '')
        .replace(/,?\s*Singapore\s*\d{6}/i, '')
        .replace(/,?\s*Singapore$/i, '')
        .trim();

      // Check if there's an existing service report (draft or submitted)
      if (job.serviceReport && Object.keys(job.serviceReport).length > 0) {
        console.log('Loading existing service report:', job.serviceReport);
        console.log('Date of Attendance from saved report:', job.serviceReport.dateOfAttendance);

        // Merge existing service report with default values
        setReportData(prev => ({
          ...prev,
          ...job.serviceReport,
          // Preserve the saved date of attendance
          dateOfAttendance: job.serviceReport.dateOfAttendance || prev.dateOfAttendance,
          workOrderNumber: job.serviceReport.workOrderNumber || job?.jobNumber || prev.workOrderNumber,
          // Ensure customer details are updated with latest info
          customerDetails: {
            ...job.serviceReport.customerDetails,
            name: job.customerId.fullName || job.customerId.name || job.serviceReport.customerDetails?.name || '',
            telephone: job.customerContactNumber || job.customerId.phone || job.serviceReport.customerDetails?.telephone || '',
            email: job.customerId.email || job.serviceReport.customerDetails?.email || '',
            address: job.serviceReport.customerDetails?.address || streetAddress,
            unitNumber: job.serviceReport.customerDetails?.unitNumber || unitNum,
            postalCode: job.serviceReport.customerDetails?.postalCode || postalCode
          }
        }));
      } else {
        // No existing service report, just populate customer details
        setReportData(prev => ({
          ...prev,
          customerDetails: {
            name: job.customerId.fullName || job.customerId.name || '',
            telephone: job.customerContactNumber || job.customerId.phone || '',
            email: job.customerId.email || '',
            address: streetAddress,
            unitNumber: unitNum,
            postalCode: postalCode
          }
        }));
      }
    }
  }, [job]);

  // Auto-calculate total charges
  useEffect(() => {
    const adHocTotal = parseFloat(reportData.charges.adHocWorksTotal) || 0;
    const partsCost = parseFloat(reportData.charges.partsMaterialsCost) || 0;
    const total = adHocTotal + partsCost;

    setReportData(prev => ({
      ...prev,
      charges: {
        ...prev.charges,
        totalAmount: total
      }
    }));
  }, [
    reportData.charges.adHocWorksTotal,
    reportData.charges.partsMaterialsCost
  ]);

  // Restore signature to canvas when loading saved draft
  useEffect(() => {
    if (reportData.customerAcknowledgement.signature && signatureRef.current) {
      // Load the saved signature image onto the canvas
      const canvas = signatureRef.current.getCanvas();
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };

      img.src = reportData.customerAcknowledgement.signature;
    }
  }, [reportData.customerAcknowledgement.signature]);

  const handleInputChange = (section, field, value) => {
    setReportData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setReportData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleChecklistChange = (mainSection, subSection, category, field, value) => {
    setReportData(prev => ({
      ...prev,
      preventiveMaintenance: {
        ...prev.preventiveMaintenance,
        [mainSection]: {
          ...prev.preventiveMaintenance[mainSection],
          [subSection]: {
            ...prev.preventiveMaintenance[mainSection][subSection],
            [field]: value
          }
        }
      }
    }));
  };

  const handleHalfYearlyChange = (field, value) => {
    setReportData(prev => ({
      ...prev,
      preventiveMaintenance: {
        ...prev.preventiveMaintenance,
        halfYearlyService: {
          ...prev.preventiveMaintenance.halfYearlyService,
          [field]: value
        }
      }
    }));
  };

  const handleMinorFixChange = (field, value) => {
    setReportData(prev => ({
      ...prev,
      preventiveMaintenance: {
        ...prev.preventiveMaintenance,
        minorHomeFixes: {
          ...prev.preventiveMaintenance.minorHomeFixes,
          [field]: value
        }
      }
    }));
  };

  const handleChargeChange = (field, value) => {
    // Allow empty string or valid numbers
    // Store as string to allow user to type freely, convert to number only when needed
    setReportData(prev => ({
      ...prev,
      charges: {
        ...prev.charges,
        [field]: value === '' ? '' : value
      }
    }));
  };

  const handleAdHocWorkChange = (field, value) => {
    setReportData(prev => ({
      ...prev,
      adHocWorks: {
        ...prev.adHocWorks,
        [field]: value
      }
    }));
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);

    try {
      toast.loading('Uploading photo...', { id: 'photo-upload' });

      const response = await api.uploadFiles('/jobs/upload-photo', formData);

      const photoUrl = response.url || response.photoUrl;

      setReportData(prev => ({
        ...prev,
        adHocWorks: {
          ...prev.adHocWorks,
          propertyConditionPhotos: [...prev.adHocWorks.propertyConditionPhotos, photoUrl]
        }
      }));

      toast.success('Photo uploaded successfully!', { id: 'photo-upload' });
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo', { id: 'photo-upload' });
    }
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setReportData(prev => ({
        ...prev,
        customerAcknowledgement: {
          ...prev.customerAcknowledgement,
          signature: ''
        }
      }));
    }
  };

  const handleSaveSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setReportData(prev => ({
        ...prev,
        customerAcknowledgement: {
          ...prev.customerAcknowledgement,
          signature: signatureData
        }
      }));
      toast.success('Signature saved');
    }
  };

  const handleSubmit = async (isDraft = false) => {
    try {
      setLoading(true);

      // Prepare payload with current signature from canvas
      let finalSignature = reportData.customerAcknowledgement.signature;

      // Save signature from canvas before submitting
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        finalSignature = signatureRef.current.toDataURL();
      }

      // Validate signature is present if not draft
      if (!isDraft && !finalSignature) {
        toast.error('Please provide customer signature');
        setLoading(false);
        return;
      }

      const payload = {
        ...reportData,
        customerAcknowledgement: {
          ...reportData.customerAcknowledgement,
          signature: finalSignature
        },
        jobId: job._id,
        isDraft
      };

      console.log('Submitting service report:', {
        jobId: job._id,
        isDraft,
        jobStatus: job.status,
        dateOfAttendance: payload.dateOfAttendance,
        workOrderNumber: payload.workOrderNumber,
        payload
      });

      toast.loading(isDraft ? 'Saving draft...' : 'Submitting service report...', {
        id: 'submit-report'
      });

      const response = await api.post(`/jobs/${job._id}/service-report`, payload);

      console.log('Service report response:', response);

      toast.success(
        isDraft ? 'Service report saved as draft' : 'Service report submitted successfully!',
        { id: 'submit-report' }
      );

      if (onSuccess) {
        onSuccess(response);
      }

      if (!isDraft) {
        onClose();
      }
    } catch (error) {
      console.error('Submit service report error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });

      const errorMessage = error.message || error.response?.data?.message || 'Failed to submit service report';

      toast.error(errorMessage, { id: 'submit-report' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">SwiftFixPro Maintenance & Jobsite Service Report</h2>
              <p className="text-orange-100 text-sm">Job #{job?.jobNumber}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-orange-700 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
            {/* Header Information */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-600" />
                Header Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Attendance
                  </label>
                  <input
                    type="date"
                    value={reportData.dateOfAttendance}
                    onChange={(e) => setReportData(prev => ({ ...prev, dateOfAttendance: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Order No.
                  </label>
                  <input
                    type="text"
                    value={reportData.workOrderNumber}
                    onChange={(e) => setReportData(prev => ({ ...prev, workOrderNumber: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={reportData.customerDetails.name}
                    onChange={(e) => handleInputChange('customerDetails', 'name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Customer Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tel
                  </label>
                  <input
                    type="tel"
                    value={reportData.customerDetails.telephone}
                    onChange={(e) => handleInputChange('customerDetails', 'telephone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={reportData.customerDetails.email}
                    onChange={(e) => handleInputChange('customerDetails', 'email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={reportData.customerDetails.address}
                    onChange={(e) => handleInputChange('customerDetails', 'address', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., 123 Orchard Road"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Number
                  </label>
                  <input
                    type="text"
                    value={reportData.customerDetails.unitNumber}
                    onChange={(e) => handleInputChange('customerDetails', 'unitNumber', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., #12-34"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value="Singapore"
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={reportData.customerDetails.postalCode}
                    onChange={(e) => handleInputChange('customerDetails', 'postalCode', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., 123456"
                    pattern="[0-9]{6}"
                  />
                </div>
              </div>
            </section>

            {/* Section A: Preventive Maintenance */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-orange-600" />
                A. Preventive Maintenance
              </h3>

              <div className="bg-gray-50 rounded-lg p-4 space-y-6">
                {/* Monthly Visual & Functional Check */}
                <div>
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={reportData.preventiveMaintenance.monthlyVisualCheck.performed}
                      onChange={(e) => handleNestedInputChange('preventiveMaintenance', 'monthlyVisualCheck', 'performed', e.target.checked)}
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500 mr-3"
                    />
                    <span className="font-bold text-gray-900">☐ Monthly Visual & Functional Check</span>
                  </label>

                  {/* Electrical Subcategory */}
                  <div className="ml-8 mb-4">
                    <h5 className="font-semibold text-gray-800 mb-2">Electrical</h5>
                    <div className="space-y-2 ml-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.lightingPoints}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'lightingPoints', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        All lighting points operational
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.switches}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'switches', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Light switches function correctly
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.powerSockets}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'powerSockets', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Power sockets tested and working
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.dbCoverSecure}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'dbCoverSecure', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Distribution Board (DB) cover is secure
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.noBurningSigns}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'noBurningSigns', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        No signs of burning/overheating at sockets or switches
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.electrical.rccbTested}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'electrical', null, 'rccbTested', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Test RCCB/ELCB trip button on DB
                      </label>
                    </div>
                  </div>

                  {/* Plumbing Subcategory */}
                  <div className="ml-8 mb-4">
                    <h5 className="font-semibold text-gray-800 mb-2">Plumbing</h5>
                    <div className="space-y-2 ml-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.exposedPipesLeaks}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'exposedPipesLeaks', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Inspect exposed pipes for leaks
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.exposedPipesCorrosion}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'exposedPipesCorrosion', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Inspect exposed pipes for corrosion
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.tapsAndFaucets}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'tapsAndFaucets', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Check taps and faucets for drips
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.toiletFlushing}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'toiletFlushing', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Check toilet flushing system
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.bidetSprayer}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'bidetSprayer', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Check bidet/sprayer for leaks
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.plumbing.drainsClear}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'plumbing', null, 'drainsClear', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Ensure drains are clear (sinks, showers, floor traps)
                      </label>
                    </div>
                  </div>

                  {/* Fixtures & Fittings Subcategory */}
                  <div className="ml-8">
                    <h5 className="font-semibold text-gray-800 mb-2">Fixtures & Fittings</h5>
                    <div className="space-y-2 ml-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings.doorHandles}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'fixturesAndFittings', null, 'doorHandles', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Door handles, hinges, and locks secure
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings.cabinetHinges}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'fixturesAndFittings', null, 'cabinetHinges', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Cabinet hinges and handles secure
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={reportData.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings.windowLatches}
                          onChange={(e) => handleChecklistChange('monthlyVisualCheck', 'fixturesAndFittings', null, 'windowLatches', e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                        />
                        Window latches and hinges secure
                      </label>
                    </div>
                  </div>
                </div>

                {/* Half-Yearly Service */}
                <div>
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={reportData.preventiveMaintenance.halfYearlyService.performed}
                      onChange={(e) => handleHalfYearlyChange('performed', e.target.checked)}
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500 mr-3"
                    />
                    <span className="font-bold text-gray-900">☐ Half-Yearly Service</span>
                  </label>

                  <div className="ml-8 space-y-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.halfYearlyService.shortCircuitTest}
                        onChange={(e) => handleHalfYearlyChange('shortCircuitTest', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Short Circuit / Insulation Test performed
                    </label>
                    {reportData.preventiveMaintenance.halfYearlyService.shortCircuitTest && (
                      <div className="ml-6 mt-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Next Scheduled:</label>
                        <input
                          type="date"
                          value={reportData.preventiveMaintenance.halfYearlyService.nextScheduled}
                          onChange={(e) => handleHalfYearlyChange('nextScheduled', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                    )}
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.halfYearlyService.acFilters}
                        onChange={(e) => handleHalfYearlyChange('acFilters', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Air-conditioner filters cleaned
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.halfYearlyService.kitchenHoodFilters}
                        onChange={(e) => handleHalfYearlyChange('kitchenHoodFilters', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Kitchen hood filters cleaned
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.halfYearlyService.smokeDetectors}
                        onChange={(e) => handleHalfYearlyChange('smokeDetectors', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Smoke/heat detectors tested
                    </label>
                  </div>
                </div>

                {/* Minor Home Fixes */}
                <div>
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={reportData.preventiveMaintenance.minorHomeFixes.performed}
                      onChange={(e) => handleMinorFixChange('performed', e.target.checked)}
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500 mr-3"
                    />
                    <span className="font-bold text-gray-900">☐ Minor Home Fixes (As required)</span>
                  </label>

                  <div className="ml-8 space-y-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.minorHomeFixes.tightenFittings}
                        onChange={(e) => handleMinorFixChange('tightenFittings', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Tighten loose fittings/fixtures
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.minorHomeFixes.replaceBulbs}
                        onChange={(e) => handleMinorFixChange('replaceBulbs', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Replace non-functioning light bulbs
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.minorHomeFixes.plumbingDrips}
                        onChange={(e) => handleMinorFixChange('plumbingDrips', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Address minor plumbing drips
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.minorHomeFixes.drainClogs}
                        onChange={(e) => handleMinorFixChange('drainClogs', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Clear simple drain clogs
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={reportData.preventiveMaintenance.minorHomeFixes.adjustDoors}
                        onChange={(e) => handleMinorFixChange('adjustDoors', e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mr-2"
                      />
                      Adjust sticking doors or cabinets
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Section B: Ad-Hoc Works */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-600" />
                B. Ad-Hoc Works
              </h3>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description:
                  </label>
                  <textarea
                    value={reportData.adHocWorks.description}
                    onChange={(e) => handleAdHocWorkChange('description', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    rows="4"
                    placeholder="Describe the ad-hoc works performed..."
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property condition before work:
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors cursor-pointer">
                      <Upload className="w-4 h-4" />
                      Upload Photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {reportData.adHocWorks.propertyConditionPhotos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {reportData.adHocWorks.propertyConditionPhotos.map((photo, index) => (
                        <img
                          key={index}
                          src={photo}
                          alt={`Property condition ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Ad-hoc works are not included in the subscription fee and are subject to separate billing.
                  </p>
                </div>
              </div>
            </section>

            {/* Section C: Charges */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-orange-600" />
                C. Charges
              </h3>

              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ● Ad-hoc works total: SGD _________
                  </label>
                  <input
                    type="number"
                    value={reportData.charges.adHocWorksTotal}
                    onChange={(e) => handleChargeChange('adHocWorksTotal', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ● Parts / materials: SGD _________
                  </label>
                  <input
                    type="number"
                    value={reportData.charges.partsMaterialsCost}
                    onChange={(e) => handleChargeChange('partsMaterialsCost', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </section>

            {/* Customer Acknowledgement */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Customer Acknowledgement
              </h3>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-4">
                  I acknowledge completion of the described works, charges (if any), and receipt of this service report.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Signature <span className="text-red-500">*</span>
                    </label>
                    <div className="border-2 border-gray-300 rounded-lg bg-white">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'w-full h-40 rounded-lg',
                          style: { touchAction: 'none' }
                        }}
                        backgroundColor="white"
                        penColor="black"
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={handleClearSignature}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveSignature}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save Signature
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Draw signature above using mouse or touch
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={reportData.customerAcknowledgement.date}
                      onChange={(e) => handleInputChange('customerAcknowledgement', 'date', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save Draft
            </button>
            <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Report
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ServiceReportForm;